// Detail panel that pops from the right when a kanban card is clicked. Shows
// the agent's full state: status transitions, event timeline, notes, contacts,
// drafts, and the scheduled-actions list. Each section is mutation-aware and
// invalidates the timeline query on success so the UI stays coherent.

import { useState, type ReactNode } from "react";
import {
  Bell,
  Calendar,
  Check,
  FileText,
  Mail,
  MessageSquare,
  UserPlus,
  X,
} from "lucide-react";
import type {
  AgentCallback,
  ApplicationDraft,
  ApplicationDraftKind,
  ApplicationEvent,
  ApplicationEventType,
  ScheduledAction,
} from "@resume-cloudflare/shared";
import { Button } from "../ui/Button";
import { Pill, type PillTone } from "../ui/Pill";
import { Sheet } from "../ui/Sheet";
import { Input } from "../ui/Input";
import { Textarea } from "../ui/Textarea";
import { Spinner } from "../ui/Spinner";
import {
  APPLICATION_STATUSES,
  useAddContact,
  useAddInterview,
  useAddNote,
  useApplicationTimeline,
  useCancelScheduled,
  useDeleteApplication,
  useSubmissionRecord,
  useUpdateApplicationStatus,
  type ApplicationRow,
  type ApplicationStatus,
  type SubmissionRecord,
} from "../../lib/applications";

const STATUS_TONE: Record<ApplicationStatus, PillTone> = {
  interested: "neutral",
  preparing: "warning",
  approved: "accent",
  applied: "info",
  interviewing: "accent",
  offer: "success",
  rejected: "danger",
  withdrawn: "neutral",
};

const EVENT_ICON: Record<ApplicationEventType, ReactNode> = {
  status_changed: <Check size={14} className="text-info-fg" />,
  note_added: <MessageSquare size={14} className="text-neutral-400" />,
  interview_added: <Calendar size={14} className="text-accent-300" />,
  contact_added: <UserPlus size={14} className="text-success-fg" />,
  draft_created: <FileText size={14} className="text-warning-fg" />,
  alarm_scheduled: <Bell size={14} className="text-neutral-400" />,
  alarm_fired: <Bell size={14} className="text-warning-fg" />,
  message_sent: <MessageSquare size={14} className="text-accent-300" />,
  answers_accepted: <Check size={14} className="text-success-fg" />,
  packet_approved: <Check size={14} className="text-accent-300" />,
  submission_recorded: <Check size={14} className="text-success-fg" />,
};

const CALLBACK_LABEL: Record<AgentCallback, string> = {
  send_followup_nudge: "Follow-up nudge",
  send_interview_thankyou: "Interview thank-you",
  send_post_rejection_retro: "Post-rejection retro",
};

export function ApplicationSheet({
  application,
  onClose,
  onDeleted,
  onStatusChange,
}: {
  application: ApplicationRow;
  onClose: () => void;
  onDeleted: () => void;
  onStatusChange: (next: ApplicationRow) => void;
}) {
  const timelineQ = useApplicationTimeline(application.id);
  const submitted = !["interested", "preparing", "approved"].includes(application.status);
  const recordQ = useSubmissionRecord(application.id, submitted);
  const update = useUpdateApplicationStatus();
  const del = useDeleteApplication();

  async function transition(status: ApplicationStatus) {
    if (status === application.status) return;
    const res = await update.mutateAsync({ id: application.id, status });
    onStatusChange(res.application);
  }

  async function remove() {
    if (!confirm(`Delete ${application.role}?`)) return;
    await del.mutateAsync(application.id);
    onDeleted();
  }

  const agentState = timelineQ.data?.agent_state ?? application.agent_state;
  const scheduled = agentState?.scheduled ?? [];

  // Session 6 PR3 — show the prep button once the user is actively interview-
  // ing OR they've scheduled an interview event (which can happen before the
  // status flips, e.g. "applied" → recruiter books a phone screen → user
  // wants to prep before bumping the status). Either signal is sufficient.
  const hasInterviewEvent = (timelineQ.data?.events ?? []).some(
    (e) => e.type === "interview_added",
  );
  const showPrepButton =
    application.status === "interviewing" || hasInterviewEvent;

  return (
    <>
      <Sheet.Header
        eyebrow={
          <span className="inline-flex items-center gap-2">
            <span className="truncate">
              {application.company_name ?? application.company_id ?? "—"}
            </span>
            {application.company_tier ? (
              <Pill tone="neutral">Tier {application.company_tier}</Pill>
            ) : null}
            <Pill tone={STATUS_TONE[application.status]} className="capitalize">
              {application.status}
            </Pill>
          </span>
        }
        title={application.role}
        description={
          <span className="inline-flex items-center gap-2">
            <span>Updated {ageLabel(application.updated_at)}</span>
            {application.listing_active ? (
              <span className="text-success-fg">· listing active</span>
            ) : (
              <span className="text-danger-fg">· listing closed</span>
            )}
          </span>
        }
      />
      <Sheet.Body className="space-y-6">
        <div className="grid gap-2">
          <Button
            variant="primary"
            className="w-full"
            onClick={() =>
              window.open(application.url, "_blank", "noopener,noreferrer")
            }
          >
            Open posting
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                window.location.href = `/applications/${application.id}/apply`;
              }}
            >
              Application assistant
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                window.location.href = `/applications/${application.id}/resume`;
              }}
            >
              Tailor resume
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                window.location.href = `/applications/${application.id}/apply-kit`;
              }}
            >
              Open apply kit
            </Button>
          </div>
          {showPrepButton ? (
            <Button
              variant="secondary"
              onClick={() => {
                window.location.href = `/applications/${application.id}/prep`;
              }}
            >
              Prep for interview
            </Button>
          ) : null}
        </div>

        {submitted && recordQ.data ? (
          <Section title="As submitted">
            <SubmissionRecordView record={recordQ.data} />
          </Section>
        ) : null}

        <Section title="Transition status">
          <div className="grid grid-cols-2 gap-2">
            {APPLICATION_STATUSES.map((s) => (
              <Button
                key={s}
                size="sm"
                variant={s === application.status ? "primary" : "secondary"}
                disabled={update.isPending || s === application.status}
                onClick={() => transition(s)}
                className="capitalize"
              >
                {s}
              </Button>
            ))}
          </div>
        </Section>

        <Section
          title="Scheduled actions"
          aside={
            scheduled.length > 0 ? (
              <span className="text-[10px] text-neutral-500">
                {scheduled.length} pending
              </span>
            ) : null
          }
        >
          <ScheduledList
            applicationId={application.id}
            scheduled={scheduled}
          />
        </Section>

        <Section title="Add note">
          <NoteForm applicationId={application.id} />
        </Section>

        <Section title="Schedule an interview">
          <InterviewForm applicationId={application.id} />
        </Section>

        <Section title="Add contact">
          <ContactForm applicationId={application.id} />
        </Section>

        <Section
          title="Drafts"
          aside={
            timelineQ.data?.drafts.length ? (
              <span className="text-[10px] text-neutral-500">
                {timelineQ.data.drafts.length}
              </span>
            ) : null
          }
        >
          <DraftsList drafts={timelineQ.data?.drafts ?? []} />
        </Section>

        <Section
          title="Timeline"
          aside={
            timelineQ.data?.events.length ? (
              <span className="text-[10px] text-neutral-500">
                {timelineQ.data.events.length}
              </span>
            ) : null
          }
        >
          {timelineQ.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <Spinner size={12} /> Loading timeline…
            </div>
          ) : timelineQ.error ? (
            <p className="text-xs text-danger-fg">
              Couldn't load timeline. {String(timelineQ.error)}
            </p>
          ) : (
            <TimelineList events={timelineQ.data?.events ?? []} />
          )}
        </Section>

        <section className="border-t border-neutral-800 pt-4">
          <Button
            variant="danger"
            className="w-full"
            loading={del.isPending}
            onClick={remove}
          >
            Delete application
          </Button>
          <button
            type="button"
            onClick={onClose}
            className="mt-2 w-full text-center text-[11px] text-neutral-500 hover:text-neutral-300"
          >
            Close panel
          </button>
        </section>
      </Sheet.Body>
    </>
  );
}

function Section({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-[11px] uppercase tracking-wider text-neutral-500">
          {title}
        </h3>
        {aside}
      </div>
      <div>{children}</div>
    </section>
  );
}

const RECORD_KIND_LABEL: Record<SubmissionRecord["items"][number]["kind"], string> = {
  resume_pdf: "Resume (PDF as submitted)",
  cover_letter_pdf: "Cover letter (PDF as submitted)",
  form: "Form",
  confirmation: "Confirmation page",
};

// The documents and form exactly as they went out, frozen at submit time, so
// an interview weeks later can be prepped from what the company actually has.
function SubmissionRecordView({ record }: { record: SubmissionRecord }) {
  const docs = record.items.filter((i) => i.kind === "resume_pdf" || i.kind === "cover_letter_pdf");
  const shots = record.items.filter((i) => i.kind === "form" || i.kind === "confirmation");
  const sub = record.submission;
  return (
    <div className="space-y-2 text-xs">
      {sub?.submitted_at ? (
        <p className="text-neutral-400">
          Submitted {dateTimeLabel(sub.submitted_at)}
          {sub.mode ? ` (${sub.mode.replace(/_/g, " ")})` : ""}
          {sub.confirmation_url ? (
            <>
              {" · "}
              <a href={sub.confirmation_url} target="_blank" rel="noreferrer" className="text-accent-300 hover:underline">
                confirmation
              </a>
            </>
          ) : null}
        </p>
      ) : null}
      {docs.length ? (
        <ul className="space-y-1">
          {docs.map((d) => (
            <li key={d.id}>
              <a href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-accent-300 hover:underline">
                <FileText size={12} /> {RECORD_KIND_LABEL[d.kind]}
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-neutral-500">No documents were frozen for this submission.</p>
      )}
      {shots.length ? (
        <div className="flex flex-wrap gap-2">
          {shots.map((s) => (
            <a key={s.id} href={s.url} target="_blank" rel="noreferrer" title={s.label ?? RECORD_KIND_LABEL[s.kind]}>
              <img
                src={s.url}
                alt={`${RECORD_KIND_LABEL[s.kind]} ${s.seq}`}
                loading="lazy"
                className="h-20 w-16 rounded border border-neutral-800 object-cover object-top"
              />
            </a>
          ))}
        </div>
      ) : null}
      {sub?.fields.length ? (
        <details>
          <summary className="cursor-pointer text-neutral-400">Form answers ({sub.fields.length})</summary>
          <dl className="mt-1 space-y-1">
            {sub.fields.map((f, i) => (
              <div key={i}>
                <dt className="text-neutral-500">{f.label}</dt>
                <dd className="whitespace-pre-wrap text-neutral-200">{f.value ?? "(blank)"}</dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}
    </div>
  );
}

function ScheduledList({
  applicationId,
  scheduled,
}: {
  applicationId: string;
  scheduled: ScheduledAction[];
}) {
  const cancel = useCancelScheduled(applicationId);
  if (scheduled.length === 0) {
    return (
      <p className="text-xs text-neutral-500">
        Nothing scheduled. Move to <em>Applied</em>, <em>Rejected</em>, or add an
        interview to arm an alarm.
      </p>
    );
  }
  return (
    <ul className="space-y-2">
      {[...scheduled]
        .sort((a, b) => a.fire_at - b.fire_at)
        .map((s) => (
          <li
            key={s.id}
            className="flex items-center justify-between gap-2 rounded border border-neutral-800 bg-neutral-900/40 px-3 py-2 text-xs"
          >
            <div className="min-w-0">
              <p className="truncate text-neutral-200">{s.label}</p>
              <p className="mt-0.5 text-[10px] text-neutral-500">
                {CALLBACK_LABEL[s.callback]} · fires{" "}
                {dateTimeLabel(s.fire_at)} ({countdown(s.fire_at)})
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              loading={cancel.isPending}
              onClick={() => cancel.mutate({ scheduledId: s.id })}
              aria-label="Cancel scheduled action"
            >
              <X size={12} />
            </Button>
          </li>
        ))}
    </ul>
  );
}

function NoteForm({ applicationId }: { applicationId: string }) {
  const [text, setText] = useState("");
  const add = useAddNote(applicationId);
  return (
    <form
      className="space-y-2"
      onSubmit={async (e) => {
        e.preventDefault();
        const trimmed = text.trim();
        if (!trimmed) return;
        await add.mutateAsync({ text: trimmed });
        setText("");
      }}
    >
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Recruiter said… / next step is…"
        className="min-h-[64px]"
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          type="submit"
          variant="primary"
          loading={add.isPending}
          disabled={text.trim().length === 0}
        >
          Add note
        </Button>
      </div>
    </form>
  );
}

function InterviewForm({ applicationId }: { applicationId: string }) {
  const [label, setLabel] = useState("");
  const [when, setWhen] = useState("");
  const [notes, setNotes] = useState("");
  const add = useAddInterview(applicationId);
  return (
    <form
      className="space-y-2"
      onSubmit={async (e) => {
        e.preventDefault();
        const trimmed = label.trim();
        if (!trimmed) return;
        const ts = new Date(when).getTime();
        if (!Number.isFinite(ts)) return;
        await add.mutateAsync({
          label: trimmed,
          scheduled_at: ts,
          notes: notes.trim() || undefined,
        });
        setLabel("");
        setWhen("");
        setNotes("");
      }}
    >
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Phone screen / Onsite — system design"
      />
      <Input
        type="datetime-local"
        value={when}
        onChange={(e) => setWhen(e.target.value)}
      />
      <Textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Loop, panel, prep notes (optional)"
        className="min-h-[48px]"
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          type="submit"
          variant="primary"
          loading={add.isPending}
          disabled={!label.trim() || !when}
        >
          Schedule
        </Button>
      </div>
    </form>
  );
}

function ContactForm({ applicationId }: { applicationId: string }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const add = useAddContact(applicationId);
  if (!open) {
    return (
      <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
        Add contact
      </Button>
    );
  }
  return (
    <form
      className="space-y-2"
      onSubmit={async (e) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) return;
        await add.mutateAsync({
          name: trimmed,
          role: role.trim() || undefined,
          email: email.trim() || undefined,
          linkedin_url: linkedin.trim() || undefined,
        });
        setName("");
        setRole("");
        setEmail("");
        setLinkedin("");
        setOpen(false);
      }}
    >
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
      />
      <Input
        value={role}
        onChange={(e) => setRole(e.target.value)}
        placeholder="Role (recruiter, hiring manager, …)"
      />
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email (optional)"
      />
      <Input
        type="url"
        value={linkedin}
        onChange={(e) => setLinkedin(e.target.value)}
        placeholder="LinkedIn URL (optional)"
      />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          size="sm"
          type="submit"
          variant="primary"
          loading={add.isPending}
          disabled={!name.trim()}
        >
          Save contact
        </Button>
      </div>
    </form>
  );
}

const DRAFT_KIND_LABEL: Record<ApplicationDraftKind, string> = {
  cover_letter: "Cover letter",
  resume_version: "Resume version",
  essay_answer: "Essay answer",
  message: "Message",
};

const DRAFT_KIND_ICON: Record<ApplicationDraftKind, ReactNode> = {
  cover_letter: <FileText size={12} />,
  resume_version: <FileText size={12} />,
  essay_answer: <MessageSquare size={12} />,
  message: <Mail size={12} />,
};

function DraftsList({ drafts }: { drafts: ApplicationDraft[] }) {
  if (drafts.length === 0) {
    return (
      <p className="text-xs text-neutral-500">
        No drafts yet. The Application assistant and Resume tailor will attach
        artefacts here.
      </p>
    );
  }
  return (
    <ul className="space-y-1">
      {drafts.map((d) => {
        const href = draftHref(d);
        const inner = (
          <span className="inline-flex items-center gap-2 text-xs text-neutral-200">
            <span className="text-neutral-400">{DRAFT_KIND_ICON[d.kind]}</span>
            <span>{DRAFT_KIND_LABEL[d.kind]}</span>
            {d.preview ? (
              <span className="truncate text-neutral-500">— {d.preview}</span>
            ) : null}
          </span>
        );
        return (
          <li
            key={d.id}
            className="rounded border border-neutral-800 bg-neutral-900/40 px-2 py-1.5"
          >
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-between gap-2 hover:opacity-80"
              >
                {inner}
                <span className="text-[10px] text-neutral-500">
                  {ageLabel(d.created_at)}
                </span>
              </a>
            ) : (
              <div className="flex items-center justify-between gap-2">
                {inner}
                <span className="text-[10px] text-neutral-500">
                  {ageLabel(d.created_at)}
                </span>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function draftHref(d: ApplicationDraft): string | null {
  if (!d.ref_id) return null;
  switch (d.kind) {
    case "cover_letter":
      return `/api/cover-letter/${d.ref_id}/pdf`;
    case "resume_version":
      return `/api/resume-versions/${d.ref_id}/pdf`;
    default:
      return null;
  }
}

function TimelineList({ events }: { events: ApplicationEvent[] }) {
  if (events.length === 0) {
    return (
      <p className="text-xs text-neutral-500">
        Nothing yet. Status transitions, notes, contacts, and alarms will appear
        here.
      </p>
    );
  }
  return (
    <ol className="space-y-1.5">
      {events.map((e) => (
        <li
          key={e.id}
          className="flex items-start gap-2 rounded border border-neutral-800/60 bg-neutral-950/40 px-2 py-1.5 text-xs"
        >
          <span className="mt-0.5 shrink-0">{EVENT_ICON[e.type]}</span>
          <div className="min-w-0 flex-1">
            <p className="text-neutral-200">{describeEvent(e)}</p>
            <p className="mt-0.5 text-[10px] text-neutral-500">
              {dateTimeLabel(e.at)}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function describeEvent(e: ApplicationEvent): string {
  const p = e.payload ?? {};
  switch (e.type) {
    case "status_changed": {
      const from = (p.from as string | null | undefined) ?? "(new)";
      const to = (p.to as string | undefined) ?? "?";
      const reason = p.reason as string | null | undefined;
      const base = `Status: ${from} → ${to}`;
      return reason ? `${base} (${reason})` : base;
    }
    case "note_added": {
      const text = (p.text as string | undefined) ?? "";
      return `Note: ${text.slice(0, 200)}${text.length > 200 ? "…" : ""}`;
    }
    case "interview_added": {
      const label = (p.label as string | undefined) ?? "Interview";
      const at = p.scheduled_at as number | undefined;
      return at
        ? `Interview added — ${label} @ ${dateTimeLabel(at)}`
        : `Interview added — ${label}`;
    }
    case "contact_added": {
      const name = (p.name as string | undefined) ?? "Contact";
      const role = p.role as string | null | undefined;
      return role ? `Contact: ${name} (${role})` : `Contact: ${name}`;
    }
    case "draft_created": {
      const kind = p.kind as ApplicationDraftKind | undefined;
      return `Draft created — ${kind ? DRAFT_KIND_LABEL[kind] : "draft"}`;
    }
    case "alarm_scheduled": {
      const label = (p.label as string | undefined) ?? "Action";
      const fireAt = p.fire_at as number | undefined;
      return fireAt
        ? `Scheduled — ${label} (fires ${dateTimeLabel(fireAt)})`
        : `Scheduled — ${label}`;
    }
    case "alarm_fired": {
      const label = (p.label as string | undefined) ?? "Action";
      return `Alarm fired — ${label}`;
    }
    case "message_sent": {
      const name = (p.contact_name as string | undefined) ?? "Contact";
      const kindRaw = (p.message_kind as string | undefined) ?? "message";
      const kindLabel = kindRaw.replace(/_/g, " ");
      return `Message sent — ${kindLabel} → ${name}`;
    }
    case "answers_accepted": {
      const n = (p.answers_marked as number | undefined) ?? 0;
      return `Application answers accepted — ${n} answer${n === 1 ? "" : "s"}`;
    }
    case "packet_approved":
      return "Packet approved for submission";
    case "submission_recorded": {
      const mode = (p.mode as string | undefined) ?? "manual";
      return mode === "manual"
        ? "Submitted"
        : `Submitted (${mode.replace(/_/g, " ")})`;
    }
  }
}

export function ageLabel(ts: number): string {
  const days = Math.floor((Date.now() - ts) / 86400_000);
  if (days <= 0) {
    const h = Math.floor((Date.now() - ts) / 3600_000);
    if (h <= 0) {
      const m = Math.floor((Date.now() - ts) / 60_000);
      if (m <= 0) return "just now";
      return m === 1 ? "1 min ago" : `${m} min ago`;
    }
    return h === 1 ? "1 hour ago" : `${h} hours ago`;
  }
  if (days === 1) return "1 day ago";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "1 month ago" : `${months} months ago`;
}

export function countdown(target: number): string {
  const delta = target - Date.now();
  if (delta <= 0) return "overdue";
  const days = Math.floor(delta / 86400_000);
  if (days >= 1) return days === 1 ? "in 1 day" : `in ${days} days`;
  const hours = Math.floor(delta / 3600_000);
  if (hours >= 1) return hours === 1 ? "in 1 hour" : `in ${hours} hours`;
  const mins = Math.floor(delta / 60_000);
  return mins <= 1 ? "in <1 min" : `in ${mins} min`;
}

function dateTimeLabel(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

